import React, { useMemo } from 'react';
import { isString } from 'lodash';

// Characters to be replaced with stylistic alternates
const alts = ['W'];

function create(tag, props) {
  return <Alt
    tag={tag}
    {...props}
  />;
}

export const H1 = p => create('h1', p);
export const H2 = p => create('h2', p);
export const H3 = p => create('h3', p);
export const H4 = p => create('h4', p);
export const H5 = p => create('h5', p);
export const H6 = p => create('h6', p);

export function Alt({
  children,
  tag,
  characters,
  dangerouslySetInnerHTML,
  ...restProps
}) {
  const pattern = useMemo(() => {
    const chars = alts.slice();

    if (characters) {
      if (isString(characters)) {
        chars.push(characters);
      } else if (Array.isArray(characters)) {
        chars.push(...characters);
      }
    }

    return new RegExp(`${chars.join('|')}`, 'g');
  }, [characters]);

  const text = children ?? dangerouslySetInnerHTML?.__html;

  if (!text) {
    return null;
  }

  const content = wrapCharacters(text, pattern);

  return !tag
    ? content
    : React.createElement(tag, { children: content, ...restProps });
}

function wrapCharacters(children, pattern) {
  if (!children) {
    return null;
  }

  return React.Children.toArray(children).map(child => {
    if (!child) {
      return child;
    }

    if (isString(child)) {
      const splitText = child.split(pattern);

      if (splitText.length <= 1) {
        return child;
      }

      const matches = child.match(pattern);

      return splitText.reduce(
        (arr, element, index) =>
          matches[index]
            ? [
              ...arr,
              element,
              <span
                key={index}
                className="alt"
              >
                {matches[index]}
              </span>
            ]
            : [...arr, element],
        []
      );
    }

    if (React.isValidElement(child) && child.props?.children) {
      return React.cloneElement(child, {
        children: wrapCharacters(child.props.children)
      });
    }

    return child;
  });
}

export default Alt;
